import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useMediaQuery } from '../../hooks/use-media-query';
import { cn } from '../../lib/utils';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import confetti from 'canvas-confetti';
import NumberFlow from '@number-flow/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export function Pricing({ plans, title = "Simple, Transparent Pricing", description }) {
  const [isMonthly, setIsMonthly] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const switchRef = useRef(null);
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const handleToggle = (checked) => {
    setIsMonthly(!checked);
    if (checked && switchRef.current) {
      const rect = switchRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;

      confetti({
        particleCount: 50,
        spread: 60,
        origin: {
          x: x / window.innerWidth,
          y: y / window.innerHeight,
        },
        colors: ['#06b6d4', '#0891b2', '#3b82f6', '#2563eb'],
        ticks: 200,
        gravity: 1.2,
        decay: 0.94,
        startVelocity: 30,
        shapes: ['circle'],
      });
    }
  };

  return (
    <div className="container py-20 px-4 mx-auto max-w-7xl">
      <div className="text-center space-y-4 mb-12">
        <h2 className="text-4xl font-bold tracking-tight sm:text-5xl text-gray-900">
          {title}
        </h2>
        {description && (
          <p className="text-gray-600 text-lg max-w-2xl mx-auto whitespace-pre-line">
            {description}
          </p>
        )}
      </div>

      <div className="flex justify-center items-center gap-3 mb-10">
        <span className={cn("font-semibold transition-colors", isMonthly ? "text-gray-900" : "text-gray-500")}>
          Monthly
        </span>
        <Switch
          ref={switchRef}
          checked={!isMonthly}
          onCheckedChange={handleToggle}
          className="data-[state=checked]:bg-cyan-600"
        />
        <span className={cn("font-semibold transition-colors", !isMonthly ? "text-gray-900" : "text-gray-500")}>
          Annual <span className="text-cyan-600">(Save 20%)</span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {plans.map((plan, index) => (
          <motion.div
            key={index}
            initial={{ y: 50, opacity: 0 }}
            whileInView={
              isDesktop
                ? {
                    y: plan.isPopular ? -20 : 0,
                    opacity: 1,
                    scale: plan.isPopular ? 1.05 : 1.0,
                  }
                : { y: 0, opacity: 1 }
            }
            viewport={{ once: true }}
            transition={{
              duration: 0.8,
              type: "spring",
              stiffness: 100,
              damping: 30,
              delay: index * 0.2,
            }}
            className={cn(
              "rounded-2xl border-2 p-8 bg-white text-center flex flex-col relative shadow-lg hover:shadow-xl transition-shadow",
              plan.isPopular ? "border-cyan-500 shadow-cyan-100" : "border-gray-200"
            )}
          >
            {plan.isPopular && (
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-cyan-600 to-blue-600 py-1 px-4 rounded-full flex items-center gap-1 shadow-lg">
                <Star className="text-white h-4 w-4 fill-current" />
                <span className="text-white text-sm font-bold">
                  BEST VALUE
                </span>
              </div>
            )}
            
            <div className="flex-1 flex flex-col">
              <p className="text-lg font-bold text-gray-900 uppercase tracking-wide">
                {plan.name}
              </p>
              
              <div className="mt-6 flex items-center justify-center gap-x-2">
                {plan.showPrice ? (
                  <>
                    <span className="text-5xl font-bold tracking-tight text-gray-900">
                      <NumberFlow
                        value={isMonthly ? Number(plan.price) : Number(plan.yearlyPrice)}
                        format={{
                          style: 'currency',
                          currency: 'USD',
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }}
                        transformTiming={{
                          duration: 500,
                          easing: 'ease-out',
                        }}
                        willChange
                      />
                    </span>
                    <span className="text-sm font-semibold leading-6 tracking-wide text-gray-500">
                      / {plan.period}
                    </span>
                  </>
                ) : (
                  <span className="text-5xl font-bold tracking-tight text-gray-900">
                    {plan.price}
                  </span>
                )}
              </div>

              <p className="text-sm leading-5 text-gray-500 mt-2">
                {isMonthly ? plan.billingInfo : plan.yearlyBillingInfo || plan.billingInfo}
              </p>

              <ul className="mt-8 gap-3 flex flex-col text-left">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>

              <hr className="w-full my-6 border-gray-200" />

              <button
                onClick={async () => {
                  if (loading || checkoutLoading) return; // Prevent double clicks
                  
                  const billingPeriod = isMonthly ? 'monthly' : 'yearly';
                  
                  // Store billing preference in localStorage
                  localStorage.setItem('billingPeriod', billingPeriod);
                  
                  // If user is already logged in, directly create checkout and go to Stripe
                  if (!loading && user) {
                    setCheckoutLoading(true);
                    toast.loading('Creating checkout session...', { id: 'checkout' });
                    try {
                      const origin = window.location.origin;
                      const token = localStorage.getItem('token');
                      
                      const response = await axios.post(`${API}/subscription/create-checkout`, {
                        origin_url: origin,
                        billing_period: billingPeriod
                      }, {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });

                      if (response.data && response.data.url) {
                        // Redirect directly to Stripe checkout
                        toast.success('Redirecting to Stripe checkout...', { id: 'checkout' });
                        setTimeout(() => {
                          window.location.href = response.data.url;
                        }, 500);
                      } else {
                        setCheckoutLoading(false);
                        toast.error('Failed to create checkout session. Please try again.', { id: 'checkout' });
                        navigate(`/subscribe?billing=${billingPeriod}`);
                      }
                    } catch (error) {
                      setCheckoutLoading(false);
                      
                      // Show error message
                      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create checkout session';
                      toast.error(`Error: ${errorMsg}`, { id: 'checkout', duration: 5000 });
                      
                      // Fallback to subscribe page if checkout creation fails
                      navigate(`/subscribe?billing=${billingPeriod}`);
                    }
                  } else if (!loading) {
                    // If not logged in, go to signup (will auto-redirect to Stripe after signup)
                    toast.info('Please sign up to continue with payment');
                    navigate(`/signup?redirect=checkout&billing=${billingPeriod}`);
                  }
                }}
                disabled={loading || checkoutLoading}
                className={cn(
                  "group relative w-full gap-2 overflow-hidden text-lg font-bold tracking-tight py-4 rounded-xl",
                  "transform-gpu ring-offset-current transition-all duration-300 ease-out",
                  plan.isPopular
                    ? "bg-gradient-to-r from-cyan-600 to-blue-600 text-white hover:from-cyan-700 hover:to-blue-700 shadow-lg hover:shadow-xl"
                    : "bg-white text-cyan-600 border-2 border-cyan-600 hover:bg-cyan-50"
                )}
              >
                {checkoutLoading ? 'Redirecting to checkout...' : plan.buttonText}
              </button>
              
              <p className="mt-4 text-sm leading-5 text-gray-500">
                {plan.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
